import { getAnticheatWeaponLabel } from './anticheatWeaponLabels';

export enum AnticheatSystemAlias {
	GODMODE = 'gm',
	TELEPORT = 'tp',
	SPEEDHACK = 'sh',
	WEAPON = 'weapon',
	PING = 'ping',
}

export enum WarnReason {
	GODMODE = 'godmode',
	HEALTH_HACK = 'health_hack',
	ARMOUR_HACK = 'armour_hack',

	TELEPORT_TO_COORDS = 'tp_to_coords',
	TELEPORT_TO_DIMENSION = 'tp_to_dimension',
	TELEPORT_INTO_LOCKED_VEHICLE = 'tp_into_locked_veh',

	SPEEDHACK = 'speedhack',
	VEHICLE_SPEEDHACK = 'veh_speedhack',

	WEAPON = 'unallowed_weapon',
	WEAPON_NORELOAD = 'weapon_noreload',
	WEAPON_INFINITY_AMMO = 'weapon_inf_ammo',
	WEAPON_CUSTOM_CLIP = 'weapon_custom_clip',
	WEAPON_UNKNOWN = 'weapon_unknown',
	WEAPON_FIRE_RATE = 'weapon_fire_rate',
	WEAPON_PELLETS = 'weapon_pellets',

	HIGH_PING = 'ping',
}

export interface IAnticheatDetectionDetails {
	from?: number;
	to?: number;
	actual?: number;
	limit?: number;
	weapon?: string;
	vehicleId?: number;
}

export const sanitizeAnticheatDetectionDetails = (value: unknown): IAnticheatDetectionDetails | undefined => {
	if (typeof value === 'string') {
		try {
			value = JSON.parse(value);
		} catch {
			return undefined;
		}
	}

	if (!value || typeof value !== 'object') {
		return undefined;
	}

	const source = value as Record<string, unknown>;
	const details: IAnticheatDetectionDetails = {};
	for (const key of ['from', 'to', 'actual', 'limit', 'vehicleId'] as const) {
		const detail = source[key];
		if (typeof detail === 'number' && Number.isFinite(detail)) {
			details[key] = detail;
		}
	}
	if (typeof source.weapon === 'string') {
		details.weapon = source.weapon.replace(/[\r\n]/g, ' ').slice(0, 64);
	}

	return Object.keys(details).length > 0 ? details : undefined;
};

export const ANTICHEAT_REASON_LABELS: Record<WarnReason, string> = {
	[WarnReason.GODMODE]: 'Неуязвимость',
	[WarnReason.HEALTH_HACK]: 'Изменение здоровья',
	[WarnReason.ARMOUR_HACK]: 'Изменение брони',
	[WarnReason.TELEPORT_TO_COORDS]: 'Телепорт по координатам',
	[WarnReason.TELEPORT_TO_DIMENSION]: 'Телепорт между измерениями',
	[WarnReason.TELEPORT_INTO_LOCKED_VEHICLE]: 'Проникновение в закрытый транспорт',
	[WarnReason.SPEEDHACK]: 'Превышение скорости пешком',
	[WarnReason.VEHICLE_SPEEDHACK]: 'Превышение скорости транспорта',
	[WarnReason.WEAPON]: 'Запрещённое оружие',
	[WarnReason.WEAPON_NORELOAD]: 'Стрельба без перезарядки',
	[WarnReason.WEAPON_INFINITY_AMMO]: 'Бесконечные патроны',
	[WarnReason.WEAPON_CUSTOM_CLIP]: 'Изменённый размер магазина',
	[WarnReason.WEAPON_UNKNOWN]: 'Неизвестное оружие',
	[WarnReason.WEAPON_FIRE_RATE]: 'Превышение скорострельности',
	[WarnReason.WEAPON_PELLETS]: 'Избыточное количество попаданий',
	[WarnReason.HIGH_PING]: 'Высокий пинг',
};

const formatNumber = (value: number, fractionDigits = 0): string => value.toFixed(fractionDigits).replace('.', ',');

export const formatAnticheatDetectionDetails = (reason: WarnReason, details?: IAnticheatDetectionDetails): string => {
	if (!details) {
		return '';
	}

	const { from, to, actual, limit, weapon, vehicleId } = details;
	const weaponLabel = weapon ? getAnticheatWeaponLabel(weapon) : undefined;
	switch (reason) {
		case WarnReason.HEALTH_HACK:
			return from !== undefined && to !== undefined
				? `Здоровье: ${formatNumber(from)} → ${formatNumber(to)}`
				: '';
		case WarnReason.ARMOUR_HACK:
			return from !== undefined && to !== undefined ? `Броня: ${formatNumber(from)} → ${formatNumber(to)}` : '';
		case WarnReason.TELEPORT_TO_COORDS:
			return actual !== undefined && limit !== undefined
				? `Дистанция: ${formatNumber(actual, 1)} м (лимит ${formatNumber(limit, 1)} м)`
				: '';
		case WarnReason.TELEPORT_TO_DIMENSION:
			return from !== undefined && to !== undefined
				? `Измерение: ${formatNumber(from)} → ${formatNumber(to)}`
				: '';
		case WarnReason.TELEPORT_INTO_LOCKED_VEHICLE:
			return vehicleId !== undefined ? `ID транспорта: ${formatNumber(vehicleId)}` : '';
		case WarnReason.SPEEDHACK:
		case WarnReason.VEHICLE_SPEEDHACK:
			return actual !== undefined && limit !== undefined
				? `Скорость: ${formatNumber(actual * 3.6, 1)} км/ч (лимит ${formatNumber(limit * 3.6, 1)} км/ч)`
				: '';
		case WarnReason.WEAPON:
		case WarnReason.WEAPON_UNKNOWN:
			return weaponLabel ? `Оружие: ${weaponLabel}` : '';
		case WarnReason.WEAPON_NORELOAD:
		case WarnReason.WEAPON_INFINITY_AMMO:
			return from !== undefined && to !== undefined
				? `Патроны: ${formatNumber(from)} → ${formatNumber(to)}${weaponLabel ? ` (${weaponLabel})` : ''}`
				: '';
		case WarnReason.WEAPON_CUSTOM_CLIP:
			return actual !== undefined && limit !== undefined
				? `Патроны: ${formatNumber(actual)} (максимум ${formatNumber(limit)})${
						weaponLabel ? `, ${weaponLabel}` : ''
				  }`
				: '';
		case WarnReason.WEAPON_FIRE_RATE:
			return actual !== undefined && limit !== undefined
				? `Интервал выстрелов: ${formatNumber(actual)} мс (минимум ${formatNumber(limit)} мс)${
						weaponLabel ? `, ${weaponLabel}` : ''
				  }`
				: '';
		case WarnReason.WEAPON_PELLETS:
			return actual !== undefined && limit !== undefined
				? `Попаданий за выстрел: ${formatNumber(actual)} (максимум ${formatNumber(limit)})${
						weaponLabel ? `, ${weaponLabel}` : ''
				  }`
				: '';
		case WarnReason.HIGH_PING:
			return actual !== undefined && limit !== undefined
				? `Пинг: ${formatNumber(actual)} мс (лимит ${formatNumber(limit)} мс)`
				: '';
		default:
			return '';
	}
};
